/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { Project } from '@/types/models';

interface GitReferenceSelectorProps {
  project: Project;
  reference?: string;
  className?: string;
  additonalReferences?: Array<{ label: string; value: string }>;
  handleOnSelect?: (e: { value: string }) => void;
  onManualRefAdd?: (ref: string) => void;
}

export const GitReferenceSelector: React.FC<GitReferenceSelectorProps> = ({
  reference,
  additonalReferences,
  project,
  className,
  onManualRefAdd,
  handleOnSelect
}) => {
  const [filter, setFilter] = useState<string>();
  const [customRef, setCustomRef] = useState<string>();

  const references = useMemo(() => {
    const refs = [
      {
        label: 'Branches',
        items: Object.keys(project?.branches || {}).map((el) => ({ label: el, value: el }))
      }
    ];
    if (project && Object.keys(project.tags).length > 0) {
      refs.push({
        label: 'Tags',
        items: Object.keys(project?.tags || {}).map((el) => ({ label: el, value: el }))
      });
    }
    if (project) {
      const { ref } = project;
      if (!(project.branches[ref] || project.tags[ref])) {
        refs.push({
          label: 'Commits',
          items: [{ label: ref, value: ref }]
        });
      }
    }
    const allValues = refs.flatMap((group) => group.items.map((item) => item.value));

    if (additonalReferences) {
      additonalReferences.forEach((ref) => {
        if (!allValues.includes(ref.value)) {
          refs.push({
            label: 'Commits',
            items: [ref]
          });
        }
      });
    }

    if (customRef) {
      if (!allValues.includes(customRef)) {
        const commitRefIndex = refs.findIndex((group) => group.label === 'Commits');
        if (commitRefIndex !== -1) {
          refs[commitRefIndex].items.push({ label: customRef, value: customRef });
        }
      }
    }
    return refs;
  }, [project, customRef, additonalReferences]);

  return (
    <Dropdown
      filter
      appendTo="self"
      className={classNames(className)}
      pt={{
        input: { style: { padding: '0.4rem', paddingLeft: '1rem' } },
        root: { style: { minWidth: '250px', height: '35px' } },
        wrapper: { style: { minWidth: '150px' } },
        itemLabel: { className: '' },
        emptyMessage: { className: 'px-0' }
      }}
      onFilter={(e) => setFilter(e.filter)}
      value={reference}
      optionGroupLabel="label"
      optionLabel="label"
      optionValue="value"
      emptyFilterMessage={
        <div
          onClick={() => {
            if (filter && onManualRefAdd === undefined) {
              setCustomRef(filter);
              handleOnSelect?.({ value: filter });
            } else if (filter && onManualRefAdd) {
              onManualRefAdd(filter);
            }
          }}
          className="hover:surface-100 w-full px-6 py-3 cursor-pointer hover:border-1"
        >
          {filter}
        </div>
      }
      onChange={handleOnSelect}
      options={references}
    />
  );
};
